import useDocumentTitle from "hooks/useDocumentTitle";
import { useUrlTab } from "hooks/useUrlTab";
import { TabPanel, TabView } from "primereact/tabview";
import {
  isRouteMissing,
  useGetClinicCopayRulesQuery,
} from "store/queries/clinicCopayRules";

import { BenefitComparisonTab } from "./BenefitComparisonTab";
import { ClinicCopayRulesTab } from "./ClinicCopayRulesTab";
import { CopayListTab } from "./CopayListTab";
import { CptGroupsTab } from "./CptGroupsTab";
import { FallbacksTab } from "./FallbacksTab";
import { LineTypesTab } from "./LineTypesTab";
import { PayerAutoSelectionTab } from "./PayerAutoSelectionTab";
import { PayerOverridesTab } from "./PayerOverridesTab";
import { RemitAccuracyTab } from "./RemitAccuracyTab";
import { ServiceTypeRulesTab } from "./ServiceTypeRulesTab";

/**
 * CLE-2792 — Eligibility: the payer-agnostic settings behind every eligibility check.
 *
 * A TABBED page. The tabs are not decoration: line types and payer overrides both belong to
 * eligibility as a whole rather than to one account or one payer, and giving the first one a page of
 * its own would have meant moving it when the second arrived — which it now has.
 *
 * Tab order (Mike, 2026-09-30): Payer Auto-Selection, Payer Benefit Overrides, Remit accuracy,
 * Engine worklist, Line Type Definitions. Payer Auto-Selection comes first and opens by default;
 * Line Type Definitions sit last, because a definition is written once per payer wording and then
 * left alone. The order the engine runs them in is not the order a person needs them. Only the
 * order and two headers changed on 2026-09-30 — every `?tab=` value is the same as before.
 *
 * CLE-2820 — SUPER ADMIN, and nothing narrower. The page used to hide itself from every super admin
 * but two named addresses while the engine was mid-cutover, and returned `null` for everyone else —
 * a blank screen at a real route. The route is `WithRoleProtection allowedRoles={[SUPER_ADMIN]}` and
 * every endpoint these tabs call requires SuperAdmin, so the body simply renders: whoever got here
 * is allowed to be here, and the API is the boundary either way.
 *
 * That now includes the "Engine worklist" tab. It was shown to one named address while its
 * owner iterated on it (a `COMPARISON_TAB_PREVIEW_EMAILS` list, 2026-09-17); on 2026-09-28 Mike opened
 * it to every super admin and the list was deleted. Every tab is now the whole SuperAdmin role — the
 * route's `WithRoleProtection allowedRoles={[SUPER_ADMIN]}` — and nothing narrower. If a tab ever
 * needs narrowing again, that is a permission, not an email list.
 *
 * CLE-2820 follow-up — that tab is now "Engine worklist". "Legacy vs Engine vs AI" named its
 * INPUTS; this names the JOB. Remit accuracy stays the measurement tab next door; this one is where
 * you go to fix things. Its `?tab=` value moved with it, from `legacy-engine-ai` to `engine-worklist`
 * — an old link opens the first tab, which is what every unknown value already did.
 *
 * CLE-2845 — "Payer Auto-Selection" (`?tab=payer-auto-selection`), the first tab: the rules that
 * switch an estimate's payer to the one the eligibility response names, and a tester for them.
 *
 * CLE-2971 — "Service Type Rules" (`?tab=service-type-rules`), right after it: the rules that pick the
 * Service Type an eligibility check runs under from the estimate's CPT codes (MRI/CT/ultrasound/X-ray),
 * for all hospitals or one, and a tester. Every other `?tab=` value is unchanged.
 *
 * CLE-3000 — "CPT Groups" (`?tab=cpt-groups`), right after it: the CPT / HCPCS code ranges behind each
 * procedure type those rules key on (Ani's "service line map"), with a read-only summary per group and a
 * classify tester. Global, SuperAdmin like every tab.
 *
 * CLE-3013 — "Fallbacks" (`?tab=fallbacks`), right after it: read only — which service line's
 * co-insurance is borrowed from which when the payer gives none (the SUBSTITUTE ladder). Every other
 * `?tab=` value is unchanged.
 *
 * CLE-3047 — Fallbacks is one view: a collapsible "How fallbacks work" section (by service line, collapsed by
 * default) above the live checks whose co-insurance or copay was substituted from another service line or taken from
 * earlier patients. The old sub-tabs' links still work: `&view=rules` opens the section expanded, `&view=checks`
 * opens it collapsed. No new top-level tab.
 *
 * CLE-2816 PR 2 — "Copay list" (`?tab=copay-list`), between CPT Groups and Fallbacks: which item of
 * UHC's "AMOUNT VARIES BY LOCATION" copay list each CPT code is priced under, for the benefit engine,
 * with a tester. Global, SuperAdmin like every tab. Every other `?tab=` value is unchanged.
 *
 * CLE-3050 — "Clinic copay rules" (`?tab=clinic-copay-rules`), right after Copay list: per payer and
 * CPT group, the extra copay a clinic visit is charged on top of the office copay. HIDDEN when the
 * API answers its GET with 404 (an API without the route). The hidden tab keeps its slot among the
 * TabView's children (`{shown && <TabPanel>}` leaves a null child, which PrimeReact skips but still
 * counts), so every other tab's index — and so its `?tab=` link — is the same either way; its own
 * link then opens the first tab, as an unknown value does.
 */
/** The `?tab=` value of each tab, in tab order. An unknown or missing value opens the first. */
export const ELIGIBILITY_TABS = [
  "payer-auto-selection",
  "service-type-rules",
  "cpt-groups",
  "copay-list",
  "clinic-copay-rules",
  "fallbacks",
  "payer-overrides",
  "remit-accuracy",
  "engine-worklist",
  "line-types",
] as const;

export function EligibilityPage() {
  useDocumentTitle("Eligibility");
  // A tab change PUSHES a history entry, so Back returns to the previous tab. The other tabs' own params
  // (the remit accuracy filters) are dropped: they belong to the tab being left.
  const { activeIndex: urlIndex, setActiveIndex: changeTab } = useUrlTab(
    ELIGIBILITY_TABS,
    { keepOtherParams: false }
  );
  // CLE-3050 — the RTK query is shared with the tab itself (one request).
  const { error: clinicCopayError } = useGetClinicCopayRulesQuery();
  const clinicCopayShown = !isRouteMissing(clinicCopayError);
  const activeIndex =
    !clinicCopayShown && ELIGIBILITY_TABS[urlIndex] === "clinic-copay-rules"
      ? 0
      : urlIndex;

  return (
    <div className="p-4" data-testid="eligibilityPage">
      <h2 className="m-0 mb-3">Eligibility</h2>
      <TabView
        data-testid="eligibilityPage.tabs"
        activeIndex={activeIndex}
        onTabChange={(event) => changeTab(event.index)}
      >
        {/*
          CLE-2845 — Payer Auto-Selection: the rules that switch an estimate's payer to the one the
          eligibility response names, and a tester that runs them on a typed-in response. First, and
          the default.
        */}
        <TabPanel header="Payer Auto-Selection">
          <PayerAutoSelectionTab />
        </TabPanel>
        {/*
          CLE-2971 — Service Type Rules: which Service Type an eligibility check runs under when the
          estimate has an MRI, CT, ultrasound or X-ray CPT code. All hospitals or one hospital.
        */}
        <TabPanel header="Service Type Rules">
          <ServiceTypeRulesTab />
        </TabPanel>
        {/*
          CLE-3000 — CPT Groups: the code ranges behind each procedure type the Service Type rules
          key on (the service line map), for every hospital at once.
        */}
        <TabPanel header="CPT Groups">
          <CptGroupsTab />
        </TabPanel>
        {/*
          CLE-2816 PR 2 — Copay list: CPT code → the item of UHC's per-procedure copay list the
          engine quotes, for every hospital at once.
        */}
        <TabPanel header="Copay list">
          <CopayListTab />
        </TabPanel>
        {/*
          CLE-3050 — Clinic copay rules: the add-on copay a clinic visit is charged for a scan or
          test, per payer and CPT group. Hidden on an API without the route; see above.
        */}
        {clinicCopayShown && (
          <TabPanel header="Clinic copay rules">
            <ClinicCopayRulesTab />
          </TabPanel>
        )}
        {/*
          CLE-3013 — Fallbacks, read only: which service line's co-insurance is borrowed from which
          when the payer gives none (CLE-3047: by service line, not by hospital).
        */}
        {/* CLE-3047 — one view: "How fallbacks work" (collapsed) above the checks list. */}
        <TabPanel header="Fallbacks">
          <FallbacksTab />
        </TabPanel>
        <TabPanel header="Payer Benefit Overrides">
          <PayerOverridesTab />
        </TabPanel>
        {/* CLE-2818 — internal measurement of the engine against 835 remits. */}
        <TabPanel header="Remit accuracy">
          <RemitAccuracyTab />
        </TabPanel>
        {/*
          CLE-2820 — the Engine worklist: the scorecard, the coverage strip and one ranked list of
          clusters to fix. Open to every super admin like the others.
        */}
        <TabPanel header="Engine worklist">
          <BenefitComparisonTab />
        </TabPanel>
        <TabPanel header="Line Type Definitions">
          <LineTypesTab />
        </TabPanel>
      </TabView>
    </div>
  );
}
